import { apiPost } from '../../generic/Api_Functions'

/**
 * S119-S122 (CU6/RN44, historia #100) — helpers for the "requires confirmation" (E-M13) mechanism
 * that a cancel/close of a fabrication order triggers when one or more external jobs are still
 * dispatched. Same MECHANISM precedent as historia #49's delivery-date confirmation (see
 * `FabricationOrder.jsx#isRequiresDeliveryConfirmationError`): RN44.1/DN-30 deliberately does NOT
 * add a headless variant — E-M13 already serves both the interactive and the non-interactive path
 * server-side.
 *
 * Contract CONFIRMED against Sun's real construction (convergence, lote 17, arbitrated 2026-09-01):
 * the 409 that trips E-M13 on a cancel/close call (`FabricationOrderRest#closeFabricationOrderStatusMulti`
 * / `#deleteFabricationOrderMulti`) carries, in `error.data`, ONLY `{ message, pendingJobs:
 * [{ jobReferenceId, label, openTripPartIds }] }` -- the SAME `ForcedReturnPendingResponse` DTO
 * `forcedReturn/confirm` itself rejects with on re-validation (S161/RN44.2). There is no `code` and
 * no `fabricationOrderId` in that body (an earlier, unverified assumption kept both) -- the error is
 * told apart from any other 409 by the SHAPE of its body (`pendingJobs` being an array), and the
 * fabrication order being acted on is supplied by the CALLER instead (`runGuarded`'s own
 * `fabricationOrderId` parameter, useForcedReturnGate.js), since the caller already knows which
 * order it is cancelling/closing. The decisions are confirmed with a single
 * `POST mos/externalJob/forcedReturn/confirm` carrying one entry per job (never a single, blanket
 * verdict for the whole fabrication order — CU6/A2); each entry's `offeredPartIds` is the
 * `openTripPartIds` of the job snapshot the USER SAW when deciding — exactly what feeds the
 * server's re-validation.
 */
export const isForcedReturnRequiredError = (error) =>
  error?.status === 409 && Array.isArray(error?.data?.pendingJobs)

export const pendingJobsFromError = (error) => error?.data?.pendingJobs || []

// `jobs` must be the snapshot the user is currently looking at (`dialog.jobs`) — `offeredPartIds`
// travels straight from each job's `openTripPartIds` in THAT snapshot, never a re-derived or cached
// value, so the server's re-validation (S161/RN44.2) compares against exactly what the user saw.
export const buildForcedReturnDecisions = (jobs, choices) =>
  jobs.map((job) => ({
    jobReferenceId: job.jobReferenceId,
    verdict: choices[job.jobReferenceId],
    offeredPartIds: job.openTripPartIds || []
  }))

export const confirmForcedReturn = (fabricationOrderId, jobs, choices) =>
  apiPost('mos/externalJob/forcedReturn/confirm', {
    fabricationOrderId,
    decisions: buildForcedReturnDecisions(jobs, choices)
  })
